import { useRef, useState } from "react";
import type { UnitGroupConfig, UnitGroupingMode } from "@autodromo/units";
import { WhatsApp } from "../../components/icons/WhatsApp";
import { ContactFormModal } from "../../components/ContactFormModal/ContactFormModal";
import {
  HierarchicalDropdown,
  HierarchicalNode,
} from "../../components/HierarchicalDropdown/HierarchicalDropdown";
import { formatPhoneNumber } from "./utils/formatPhoneNumber";
import { buildWhatsappUrl } from "./utils/buildWhatsappUrl";
import { AntiBotMeta } from "../../constants/antiBot";
import { resolveWhatsappContext } from "../../utils/interpolateVariable";
import { openExternalTab } from "../../utils/externalTab";
import { getUnitDisplayName } from "../../utils/unit";
import { useFormUnits } from "../../hooks/useFormUnits";
import { useRendererApi } from "../../hooks/rendererApi/useRendererApi";
import type { useGlobalQueryParams as useGlobalQueryParamsHook } from "../../hooks/queryParams/useGlobalQueryParams";

import {
  ContactFormContentSelectOption,
  ContactFormSubmitPayload,
} from "../ContactFormModal/ContactFormModal.types";
import type { PageEntry } from "../../utils/pageEntry";

export interface WhatsappHierarchyNode {
  id: string;
  name: string;
  phoneNumber: string | null;
  message: string | null;
  hasForm?: boolean;
  unitId?: string | null;
  teamId?: string | null;
  moduleId?: string | null;
  contentTypeId?: string | null;
  contentSelectLabel?: string | null;
  module?: { id: string; title: string } | null;
  order?: number;
  parentId?: string | null;
  children?: WhatsappHierarchyNode[];
}

export interface WhatsappDropdownProps {
  items: WhatsappHierarchyNode[];
  onClick?: (phoneNumber: string, item?: WhatsappHierarchyNode) => void;
  primaryColor?: string;
  className?: string;
  message?: string;
  submitUrl?: string;
  antiBot: AntiBotMeta | null;
  accountId?: string;
  siteId?: string;
  product?: string;
  isEditor?: boolean;
  units?: { id: string; name: string; displayName: string }[];
  unitGroupingMode?: UnitGroupingMode | null;
  unitGroups?: UnitGroupConfig[] | null;
  useGlobalQueryParams?: typeof useGlobalQueryParamsHook;
  pageEntry?: PageEntry | null;
  contentSelectLabel?: string;
}

const toHierarchicalNode = (node: WhatsappHierarchyNode): HierarchicalNode => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isLeaf = !hasChildren;
  return {
    id: node.id,
    name: node.name,
    details:
      isLeaf && node.phoneNumber ? formatPhoneNumber(node.phoneNumber) : null,
    children: hasChildren ? node.children!.map(toHierarchicalNode) : undefined,
    meta: node,
  };
};

export const WhatsappDropdown = ({
  items,
  onClick,
  primaryColor,
  className,
  message,
  submitUrl,
  antiBot,
  accountId,
  siteId,
  product,
  units,
  unitGroupingMode,
  unitGroups,
  useGlobalQueryParams,
  isEditor = false,
  pageEntry,
  contentSelectLabel = "CONTEÚDO",
}: WhatsappDropdownProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<WhatsappHierarchyNode | null>(null);
  const [contentOptions, setContentOptions] = useState<
    ContactFormContentSelectOption[]
  >([]);
  const { units: formUnits, source: formUnitsSource } = useFormUnits({
    channelUnits: units,
    unitGroupingMode,
    unitGroups,
    entryUnitIds: pageEntry?.unitIds,
    useGlobalQueryParams,
  });
  const rendererApi = useRendererApi();
  const openItemIdRef = useRef<string | null>(null);

  if (!items || items.length === 0) {
    return null;
  }

  const hierarchicalItems = items.map(toHierarchicalNode);

  const loadContentOptions = async (item: WhatsappHierarchyNode) => {
    openItemIdRef.current = item.id;
    setContentOptions([]);

    if (isEditor || !item.contentTypeId) return;

    const data = await rendererApi.fetchWhatsappCollectionEntries({ itemId: item.id });

    if (!data || openItemIdRef.current !== item.id) return;

    setContentOptions(Array.isArray(data.options) ? data.options : []);
  };

  const handleLeafClick = (node: HierarchicalNode) => {
    const original = node.meta as WhatsappHierarchyNode;

    if (!original.phoneNumber) return;

    if (original.hasForm) {
      setSelectedItem(original);
      setIsModalOpen(true);
      void loadContentOptions(original);
    } else {
      const rawMsg = original.message ?? message ?? "";
      const { interpolatedMessage, trackingPayload } = resolveWhatsappContext({
        rawMessage: rawMsg,
        units,
      });
      const url = buildWhatsappUrl(original.phoneNumber, interpolatedMessage);

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("whatsapp_click", { detail: trackingPayload }),
        );
        openExternalTab({ url });
      }
      onClick?.(original.phoneNumber, original);
    }
  };

  const buildRedirectUrl = (
    formPayload?: ContactFormSubmitPayload,
  ): string | null => {
    if (!selectedItem?.phoneNumber) return null;

    const rawMsg = selectedItem.message ?? message ?? "";
    const { interpolatedMessage } = resolveWhatsappContext({
      rawMessage: rawMsg,
      formPayload,
      units,
    });

    return buildWhatsappUrl(selectedItem.phoneNumber, interpolatedMessage);
  };

  const handleFormSubmit = (formPayload?: ContactFormSubmitPayload) => {
    if (isEditor) {
      handleModalClose();
      return;
    }

    if (selectedItem?.phoneNumber) {
      const rawMsg = selectedItem.message ?? message ?? "";
      const { trackingPayload } = resolveWhatsappContext({
        rawMessage: rawMsg,
        formPayload,
        units,
      });

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("whatsapp_click", { detail: trackingPayload }),
        );
      }
      onClick?.(selectedItem.phoneNumber, selectedItem);
    }

    handleModalClose();
  };

  const handleModalClose = () => {
    openItemIdRef.current = null;
    setIsModalOpen(false);
    setSelectedItem(null);
    setContentOptions([]);
  };

  const formUnitOptions = formUnits.map((unit) => ({
    id: unit.id,
    label: getUnitDisplayName({ unit }),
  }));
  const unitOptions =
    formUnitsSource === "channel"
      ? formUnitOptions.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
      : formUnitOptions;

  const isPageEntryFromSelectedCollection = Boolean(
    selectedItem?.contentTypeId &&
      pageEntry?.contentTypeId === selectedItem.contentTypeId &&
      pageEntry?.id,
  );

  return (
    <>
      <HierarchicalDropdown
        items={hierarchicalItems}
        primaryColor={primaryColor}
        className={className}
        leafIcon={<WhatsApp color="#475569" />}
        onLeafClick={handleLeafClick}
        isEditor={isEditor}
      />

      {selectedItem && (
        <ContactFormModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleFormSubmit}
          resolveRedirectUrl={isEditor ? undefined : buildRedirectUrl}
          submitUrl={submitUrl}
          antiBot={antiBot}
          accountId={accountId}
          siteId={siteId}
          product={product}
          teamId={selectedItem.teamId ?? undefined}
          unitId={selectedItem.unitId ?? undefined}
          unitOptions={selectedItem.unitId ? null : unitOptions}
          moduleId={selectedItem.moduleId ?? undefined}
          moduleName={selectedItem.module?.title ?? undefined}
          bait={{
            type: "whatsapp",
            id: selectedItem.id,
            label: selectedItem.name,
          }}
          contentSelect={
            contentOptions.length > 0
              ? {
                  label: (
                    selectedItem.contentSelectLabel?.trim() ||
                    contentSelectLabel
                  ).toUpperCase(),
                  options: contentOptions,
                  preselectedId: isPageEntryFromSelectedCollection
                    ? pageEntry?.id
                    : null,
                }
              : null
          }
          title="Fale por WhatsApp"
          submitLabel="iniciar conversa"
        />
      )}
    </>
  );
};
